import { supabase } from '../lib/supabase.js'

/** True when the inclusive end date has passed (campaign no longer accepting pledges). */
export function isCampaignPastEndDate(endDate) {
  if (!endDate) return false
  const endDay = String(endDate).split('T')[0]
  const today = new Date().toISOString().split('T')[0]
  return today > endDay
}

function todayISO() {
  return new Date().toISOString().split('T')[0]
}

function effectiveCampaignStatus(status, endDate) {
  if (status === 'active' && isCampaignPastEndDate(endDate)) return 'completed'
  return status
}

// Standard select for works with joined data
// NOTE: videos table uses polymorphic parent_id (no FK), so we fetch videos separately
const WORK_SELECT = `
  *,
  work_genres (
    genre:genre_id (
      id,
      name,
      slug
    )
  ),
  publisher:publisher_id (
    id,
    company_name,
    brand_logo_url,
    brand_slug
  )
`

/**
 * Fetch videos for a list of work IDs and return a map { workId: [videos] }
 */
async function fetchVideosForWorks(workIds) {
  if (!workIds.length) return {}
  try {
    const { data, error } = await supabase
      .from('videos')
      // NOTE: mux_playback_id is intentionally NOT selected — it is gated behind
      // the get_video_playback RPC (subscription / publisher-ownership check).
      .select('id, mux_asset_id, mux_status, parent_type, parent_id')
      .eq('parent_type', 'work')
      .in('parent_id', workIds)

    if (error) {
      console.error('Error fetching videos:', error)
      return {}
    }

    const map = {}
    for (const v of (data || [])) {
      if (!map[v.parent_id]) map[v.parent_id] = []
      map[v.parent_id].push(v)
    }
    return map
  } catch {
    return {}
  }
}

/**
 * Transform a raw work row (with joins) into the shape the UI expects.
 * `videos` is an optional array of video rows for this work.
 */
function transformWork(item, videos = []) {
  const genres = (item.work_genres || []).map(wg => wg.genre).filter(Boolean)
  const genreNames = genres.map(g => g.name)
  const video = videos.find(v => v.mux_status === 'ready') || videos[0] || null
  // Playback IDs are never sent to browse pages; thumbnails come from stored artwork.
  const thumbnail = item.poster_url || item.backdrop_url || null

  return {
    id: item.id,
    title: item.title || 'Untitled',
    slug: item.slug,
    description: item.description || '',
    type: item.type,
    status: item.status,
    thumbnail,
    poster_url: item.poster_url,
    backdrop_url: item.backdrop_url,
    mux_playback_id: null, // populated post-gate via get_video_playback RPC on the watch page
    video_id: video?.id || null,
    creator: {
      name: item.publisher?.company_name || 'Kinora Creator',
      avatar: item.publisher?.brand_logo_url || null,
      slug: item.publisher?.brand_slug || null
    },
    tags: genreNames,
    genres: genreNames,
    genreObjects: genres,
    views: item.view_count || 0,
    view_count: item.view_count || 0,
    duration: item.duration_seconds ? formatDuration(item.duration_seconds) : null,
    duration_seconds: item.duration_seconds,
    releaseDate: item.release_date ? formatDate(item.release_date) : null,
    release_date: item.release_date,
    release_year: item.release_date ? new Date(item.release_date).getFullYear() : null,
    isNew: isRecentlyReleased(item.release_date),
    quality: video?.mux_status === 'ready' ? 'HD' : null,
    videoCount: item.type === 'tv_show' ? (item.total_episodes || 0) : undefined,
    progress: 0
  }
}

function formatDuration(seconds) {
  if (!seconds) return null
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  if (h > 0) return `${h}h ${m}m`
  return `${m} min`
}

function formatDate(dateString) {
  if (!dateString) return null
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })
}

function isRecentlyReleased(dateString) {
  if (!dateString) return false
  const daysDiff = (Date.now() - new Date(dateString).getTime()) / (1000 * 60 * 60 * 24)
  return daysDiff <= 30
}

function formatNumber(num) {
  if (num >= 1_000_000_000) return (num / 1_000_000_000).toFixed(1) + 'B'
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + 'M'
  if (num >= 1_000) return (num / 1_000).toFixed(1) + 'K'
  return num.toString()
}

/**
 * Helper: transform an array of raw work rows, fetching their videos in one batch.
 */
async function transformWorksWithVideos(rows) {
  if (!rows.length) return []
  const ids = rows.map(r => r.id)
  const videosMap = await fetchVideosForWorks(ids)
  return rows.map(r => transformWork(r, videosMap[r.id] || []))
}

export const contentService = {

  /**
   * Get all genres from the database
   */
  async getGenres() {
    try {
      const { data, error } = await supabase
        .from('genres')
        .select('id, name, slug')
        .order('name')

      if (error) {
        console.error('Error fetching genres:', error)
        return []
      }
      return data || []
    } catch (error) {
      console.error('Error in getGenres:', error)
      return []
    }
  },

  /**
   * Get recent/all published content with optional genre and type filters
   * @param {string|null} type - 'movie' or 'tv_show' (works.type enum)
   */
  async getRecentContent({ genre = null, limit = 20, status = 'published', sortBy = 'newest', type = null } = {}) {
    try {
      let query = supabase
        .from('works')
        .select(WORK_SELECT)
        .eq('status', status)
        .limit(limit)

      if (type === 'movie' || type === 'tv_show') {
        query = query.eq('type', type)
      }

      // Sort
      if (sortBy === 'views' || sortBy === 'popular') {
        query = query.order('view_count', { ascending: false, nullsFirst: false })
      } else {
        query = query.order('created_at', { ascending: false })
      }

      const { data, error } = await query

      if (error) {
        console.error('Error fetching recent content:', error)
        return []
      }

      let works = await transformWorksWithVideos(data || [])

      // Client-side genre filter (Supabase doesn't easily filter on nested joins)
      if (genre) {
        works = works.filter(w =>
          w.tags.some(t => t.toLowerCase() === genre.toLowerCase())
        )
      }

      return works
    } catch (error) {
      console.error('Error in getRecentContent:', error)
      return []
    }
  },

  /**
   * Get a single work by slug
   */
  async getWorkBySlug(slug) {
    try {
      const { data, error } = await supabase
        .from('works')
        .select(`
          ${WORK_SELECT},
          work_contributors (
            role,
            character_name,
            contributor:contributor_id (
              id,
              name,
              profile_image_url,
              biography,
              user_id
            )
          )
        `)
        .eq('slug', slug)
        .single()

      if (error) {
        console.error('Error fetching work by slug:', error)
        return null
      }

      const videosMap = await fetchVideosForWorks([data.id])
      const work = transformWork(data, videosMap[data.id] || [])

      // Add contributors
      work.contributors = (data.work_contributors || []).map(wc => ({
        id: wc.contributor?.id,
        name: wc.contributor?.name,
        role: wc.role,
        character_name: wc.character_name,
        profile_image_url: wc.contributor?.profile_image_url,
        biography: wc.contributor?.biography,
        user_id: wc.contributor?.user_id
      }))

      // For TV shows, fetch seasons and episodes
      if (data.type === 'tv_show') {
        work.seasons = await this.getSeasonsWithEpisodes(data.id)
      }

      return work
    } catch (error) {
      console.error('Error in getWorkBySlug:', error)
      return null
    }
  },

  /**
   * Get a single work by ID
   */
  async getWorkById(workId) {
    try {
      const { data, error } = await supabase
        .from('works')
        .select(`
          ${WORK_SELECT},
          work_contributors (
            role,
            character_name,
            contributor:contributor_id (
              id,
              name,
              profile_image_url,
              biography,
              user_id
            )
          )
        `)
        .eq('id', workId)
        .single()

      if (error) {
        console.error('Error fetching work by id:', error)
        return null
      }

      const videosMap = await fetchVideosForWorks([data.id])
      const work = transformWork(data, videosMap[data.id] || [])

      work.contributors = (data.work_contributors || []).map(wc => ({
        id: wc.contributor?.id,
        name: wc.contributor?.name,
        role: wc.role,
        character_name: wc.character_name,
        profile_image_url: wc.contributor?.profile_image_url,
        biography: wc.contributor?.biography,
        user_id: wc.contributor?.user_id
      }))

      if (data.type === 'tv_show') {
        work.seasons = await this.getSeasonsWithEpisodes(data.id)
      }

      return work
    } catch (error) {
      console.error('Error in getWorkById:', error)
      return null
    }
  },

  /**
   * Get seasons with episodes for a TV show
   */
  async getSeasonsWithEpisodes(workId) {
    try {
      const { data: seasons, error: seasonsError } = await supabase
        .from('seasons')
        .select('id, season_number, title, description')
        .eq('work_id', workId)
        .order('season_number')

      if (seasonsError || !seasons?.length) return []

      const seasonIds = seasons.map(s => s.id)
      const { data: episodes, error: epError } = await supabase
        .from('episodes_new')
        .select('*')
        .in('season_id', seasonIds)
        .order('episode_number')

      if (epError) {
        console.error('Error fetching episodes:', epError)
        return seasons.map(s => ({ ...s, episodes: [] }))
      }

      // Fetch videos for episodes separately (polymorphic parent_id, no FK)
      const episodeIds = (episodes || []).map(ep => ep.id)
      let epVideosMap = {}
      if (episodeIds.length) {
        try {
          const { data: epVids } = await supabase
            .from('videos')
            // mux_playback_id is gated behind the get_video_playback RPC
            .select('id, mux_status, parent_type, parent_id')
            .eq('parent_type', 'episode')
            .in('parent_id', episodeIds)
          for (const v of (epVids || [])) {
            if (!epVideosMap[v.parent_id]) epVideosMap[v.parent_id] = []
            epVideosMap[v.parent_id].push(v)
          }
        } catch { /* ignore */ }
      }

      return seasons.map(season => ({
        ...season,
        episodes: (episodes || [])
          .filter(ep => ep.season_id === season.id)
          .map(ep => {
            const vids = epVideosMap[ep.id] || []
            const vid = vids.find(v => v.mux_status === 'ready') || vids[0] || null
            return {
              id: ep.id,
              title: ep.title || `Episode ${ep.episode_number}`,
              description: ep.description,
              episode_number: ep.episode_number,
              season_number: season.season_number,
              season_id: ep.season_id,
              duration_seconds: ep.duration_seconds,
              mux_playback_id: null, // fetched post-gate via get_video_playback RPC
              video_id: vid?.id || null,
              thumbnail: null
            }
          })
      }))
    } catch (error) {
      console.error('Error in getSeasonsWithEpisodes:', error)
      return []
    }
  },

  /**
   * Get featured content for hero carousel
   */
  async getFeaturedContent(limit = 5) {
    try {
      const { data, error } = await supabase
        .from('works')
        .select(WORK_SELECT)
        .eq('status', 'published')
        .order('view_count', { ascending: false, nullsFirst: false })
        .limit(limit)

      if (error) {
        console.error('Error fetching featured content:', error)
        return []
      }

      const works = await transformWorksWithVideos(data || [])
      return works.map((w) => ({
        id: w.id,
        title: w.title,
        description: w.description || '',
        backgroundImage: w.backdrop_url || w.poster_url || null,
        genre: ['Featured'],
        rating: null,
        year: w.release_date ? new Date(w.release_date).getFullYear() : null,
        duration: w.type === 'movie' ? 'Movie' : 'Series',
        views: formatNumber(w.view_count || 0),
        isNew: isRecentlyReleased(w.release_date),
        work_id: w.id,
        slug: w.slug,
        publisher: {
          name: w.publisher?.company_name || 'Kinora Creator',
          logo: w.publisher?.brand_logo_url || null
        }
      }))
    } catch (error) {
      console.error('Error in getFeaturedContent:', error)
      return []
    }
  },

  /**
   * Search content by title and description
   * @param {string|null} type - optional 'movie' or 'tv_show' filter
   */
  async searchContent(query, limit = 20, type = null) {
    try {
      // Strip PostgREST filter-grammar metacharacters before interpolating
      // into the .or(...) string (they cannot be escaped server-side).
      const sanitizedQuery = String(query).replace(/[,().%"]/g, ' ').replace(/\s+/g, ' ').trim()
      if (!sanitizedQuery) return []

      let dbQuery = supabase
        .from('works')
        .select(WORK_SELECT)
        .eq('status', 'published')
        .or(`title.ilike.%${sanitizedQuery}%,description.ilike.%${sanitizedQuery}%`)
        .order('view_count', { ascending: false, nullsFirst: false })
        .limit(limit)

      if (type === 'movie' || type === 'tv_show') {
        dbQuery = dbQuery.eq('type', type)
      }

      const { data, error } = await dbQuery

      if (error) {
        console.error('Error searching content:', error)
        return []
      }

      return await transformWorksWithVideos(data || [])
    } catch (error) {
      console.error('Error in searchContent:', error)
      return []
    }
  },

  /**
   * Get related works (same genres)
   */
  async getRelatedContent(workId, genreNames = [], limit = 6) {
    try {
      const { data, error } = await supabase
        .from('works')
        .select(WORK_SELECT)
        .eq('status', 'published')
        .neq('id', workId)
        .limit(limit)
        .order('view_count', { ascending: false, nullsFirst: false })

      if (error) {
        console.error('Error fetching related content:', error)
        return []
      }

      let works = await transformWorksWithVideos(data || [])

      // Prioritize works that share genres
      if (genreNames.length > 0) {
        works.sort((a, b) => {
          const aMatch = a.tags.filter(t => genreNames.includes(t)).length
          const bMatch = b.tags.filter(t => genreNames.includes(t)).length
          return bMatch - aMatch
        })
      }

      return works
    } catch (error) {
      console.error('Error in getRelatedContent:', error)
      return []
    }
  },

  /**
   * Get continue watching for a user
   */
  async getContinueWatching(userId, limit = 10) {
    try {
      const { data, error } = await supabase
        .from('user_watch_history')
        .select(`
          *,
          episode:episode_new_id (
            id,
            title,
            episode_number,
            season_id,
            season:season_id (
              season_number
            )
          )
        `)
        .eq('user_id', userId)
        .or('completed.eq.false,completed.is.null')
        .gt('watch_duration_seconds', 30)
        .order('last_watched_at', { ascending: false, nullsFirst: false })
        .limit(limit)

      if (error) {
        console.error('Error fetching continue watching:', error)
        return []
      }

      if (!data?.length) return []

      // work_id is directly on every row now
      const workIds = [...new Set(data.map(d => d.work_id).filter(Boolean))]
      if (!workIds.length) return []

      const { data: works } = await supabase
        .from('works')
        .select(WORK_SELECT)
        .in('id', workIds)

      const transformedWorks = await transformWorksWithVideos(works || [])
      const worksMap = {}
      transformedWorks.forEach(w => { worksMap[w.id] = w })

      // Deduplicate by work ID (keep the most recent entry per work)
      const seen = new Set()
      return data
        .map(entry => {
          const workId = entry.work_id
          if (!workId || seen.has(workId)) return null
          seen.add(workId)
          const work = worksMap[workId]
          if (!work) return null
          const totalDur = entry.total_duration_seconds || 1
          return {
            ...work,
            progress: Math.min(Math.round((entry.watch_duration_seconds / totalDur) * 100), 100),
            lastWatchedAt: entry.last_watched_at
          }
        })
        .filter(Boolean)
    } catch (error) {
      console.error('Error in getContinueWatching:', error)
      return []
    }
  },

  /**
   * Get trending content
   */
  async getTrendingContent(limit = 10) {
    try {
      const { data, error } = await supabase
        .from('works')
        .select(WORK_SELECT)
        .eq('status', 'published')
        .order('view_count', { ascending: false, nullsFirst: false })
        .limit(limit)

      if (error) {
        console.error('Error fetching trending:', error)
        return []
      }

      const trendingWorks = await transformWorksWithVideos(data || [])
      return trendingWorks.map((w, index) => ({
        id: w.id,
        title: w.title,
        slug: w.slug,
        thumbnail: w.thumbnail,
        episodes: w.type === 'tv_show' ? `${w.videoCount || 0} EP` : 'Movie',
        rating: w.view_count > 0 ? Math.min(99, Math.round(w.view_count / 100)) : 0,
        category: w.tags?.[0] || 'General',
        trending: `#${index + 1}`,
      }))
    } catch (error) {
      console.error('Error in getTrendingContent:', error)
      return []
    }
  },

  /**
   * Get platform statistics
   */
  async getPlatformStats() {
    try {
      const [
        { count: contentCount },
        { count: publisherCount },
        { count: userCount }
      ] = await Promise.all([
        supabase.from('works').select('*', { count: 'exact', head: true }).eq('status', 'published'),
        supabase.from('publishers').select('*', { count: 'exact', head: true }),
        supabase.from('profiles').select('*', { count: 'exact', head: true })
      ])

      return {
        totalUsers: formatNumber(userCount || 0),
        totalCreators: formatNumber(publisherCount || 0),
        totalVideos: formatNumber(contentCount || 0),
        totalViews: '0'
      }
    } catch (error) {
      console.error('Error in getPlatformStats:', error)
      return { totalUsers: '0', totalCreators: '0', totalVideos: '0', totalViews: '0' }
    }
  },

  /**
   * Get all TV shows
   */
  async getAllSeries({ limit = 20, status = 'published' } = {}) {
    try {
      const { data, error } = await supabase
        .from('works')
        .select(WORK_SELECT)
        .eq('type', 'tv_show')
        .eq('status', status)
        .order('created_at', { ascending: false })
        .limit(limit)

      if (error) {
        console.error('Error fetching series:', error)
        return []
      }

      return await transformWorksWithVideos(data || [])
    } catch (error) {
      console.error('Error in getAllSeries:', error)
      return []
    }
  },

  /**
   * Get crowdfunding campaigns
   */
  async getCrowdfundingProjects({ limit = 20, status = 'active' } = {}) {
    try {
      let query = supabase
        .from('crowdfunding_campaigns')
        .select(`
          *,
          publisher:publisher_id (
            id,
            company_name,
            brand_logo_url,
            is_verified
          ),
          work:work_id (
            id,
            title,
            slug,
            poster_url,
            backdrop_url
          )
        `)
        .eq('status', status)

      if (status === 'active') {
        query = query.gte('end_date', todayISO())
      }

      const { data, error } = await query
        .order('created_at', { ascending: false })
        .limit(limit)

      if (error) {
        console.error('Error fetching crowdfunding campaigns:', error)
        return []
      }

      return (data || [])
        .filter(campaign => status !== 'active' || !isCampaignPastEndDate(campaign.end_date))
        .map(campaign => ({
        id: campaign.id,
        title: campaign.title,
        description: campaign.description,
        image: campaign.campaign_image_urls?.[0] || campaign.work?.poster_url || null,
        goal: campaign.goal_slices,
        raised: campaign.total_slices_raised,
        backerCount: campaign.backer_count,
        progressPercentage: campaign.goal_slices > 0
          ? Math.round((campaign.total_slices_raised / campaign.goal_slices) * 100)
          : 0,
        endDate: campaign.end_date,
        tags: [],
        creator: {
          name: campaign.publisher?.company_name || 'Anonymous Publisher',
          avatar: campaign.publisher?.brand_logo_url || null,
          isVerified: campaign.publisher?.is_verified || false
        },
        status: effectiveCampaignStatus(campaign.status, campaign.end_date),
        workId: campaign.work_id,
        workSlug: campaign.work?.slug,
        trailerUrl: campaign.trailer_url,
        hasPilotVideo: campaign.has_pilot_video
      }))
    } catch (error) {
      console.error('Error in getCrowdfundingProjects:', error)
      return []
    }
  },

  /**
   * Get a single crowdfunding campaign by ID, including updates
   */
  async getCrowdfundingProject(id) {
    try {
      const { data, error } = await supabase
        .from('crowdfunding_campaigns')
        .select(`
          *,
          publisher:publisher_id (
            id,
            company_name,
            brand_logo_url,
            is_verified
          ),
          work:work_id (
            id,
            title,
            slug,
            poster_url,
            backdrop_url
          ),
          campaign_updates (
            id,
            title,
            body,
            created_at
          )
        `)
        .eq('id', id)
        .single()

      if (error || !data) {
        console.error('Error fetching campaign:', error)
        return null
      }

      const status = effectiveCampaignStatus(data.status, data.end_date)

      return {
        id: data.id,
        title: data.title,
        description: data.description,
        image: data.campaign_image_urls?.[0] || data.work?.poster_url || null,
        backdrop: data.work?.backdrop_url || data.campaign_image_urls?.[0] || null,
        goal: data.goal_slices,
        raised: data.total_slices_raised,
        backerCount: data.backer_count,
        progressPercentage: data.goal_slices > 0
          ? Math.min(Math.round((data.total_slices_raised / data.goal_slices) * 100), 100)
          : 0,
        startDate: data.start_date,
        endDate: data.end_date,
        status,
        creator: {
          name: data.publisher?.company_name || 'Anonymous Publisher',
          avatar: data.publisher?.brand_logo_url || null,
          isVerified: data.publisher?.is_verified || false
        },
        work: data.work ? {
          id: data.work.id,
          title: data.work.title,
          slug: data.work.slug,
        } : null,
        updates: (data.campaign_updates || []).sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        ),
        trailerUrl: data.trailer_url,
        hasPilotVideo: data.has_pilot_video
      }
    } catch (error) {
      console.error('Error in getCrowdfundingProject:', error)
      return null
    }
  },

  /**
   * Get user's watchlist (works they saved)
   */
  async getWatchlist(userId) {
    try {
      const { data, error } = await supabase
        .from('user_watchlist')
        .select('work_id, added_at')
        .eq('user_id', userId)
        .order('added_at', { ascending: false })

      if (error) {
        console.error('Error fetching watchlist:', error)
        return []
      }

      if (!data?.length) return []

      const workIds = data.map(d => d.work_id)
      const { data: works } = await supabase
        .from('works')
        .select(WORK_SELECT)
        .in('id', workIds)

      const transformedWorks = await transformWorksWithVideos(works || [])
      const worksMap = {}
      transformedWorks.forEach(w => { worksMap[w.id] = w })

      return data
        .map(entry => {
          const work = worksMap[entry.work_id]
          if (!work) return null
          return { ...work, addedAt: entry.added_at }
        })
        .filter(Boolean)
    } catch (error) {
      console.error('Error in getWatchlist:', error)
      return []
    }
  },

  /**
   * Remove a work from user's watchlist
   */
  async removeFromWatchlist(userId, workId) {
    try {
      const { error } = await supabase
        .from('user_watchlist')
        .delete()
        .eq('user_id', userId)
        .eq('work_id', workId)

      if (error) {
        console.error('Error removing from watchlist:', error)
        return false
      }
      return true
    } catch (error) {
      console.error('Error in removeFromWatchlist:', error)
      return false
    }
  },

  /**
   * Get user's full watch history (including completed)
   */
  async getWatchHistory(userId, limit = 50) {
    try {
      const { data, error } = await supabase
        .from('user_watch_history')
        .select(`
          *,
          episode:episode_new_id (
            id,
            title,
            episode_number,
            season_id,
            season:season_id (
              season_number
            )
          )
        `)
        .eq('user_id', userId)
        .order('last_watched_at', { ascending: false, nullsFirst: false })
        .limit(limit)

      if (error) {
        console.error('Error fetching watch history:', error)
        return []
      }

      if (!data?.length) return []

      // work_id is directly on every row now
      const workIds = [...new Set(data.map(d => d.work_id).filter(Boolean))]
      if (!workIds.length) return []

      const { data: works } = await supabase
        .from('works')
        .select(WORK_SELECT)
        .in('id', workIds)

      const transformedWorks = await transformWorksWithVideos(works || [])
      const worksMap = {}
      transformedWorks.forEach(w => { worksMap[w.id] = w })

      return data
        .map(entry => {
          const work = worksMap[entry.work_id]
          if (!work) return null
          const totalDur = entry.total_duration_seconds || 1
          return {
            ...work,
            episodeTitle: entry.episode?.title || null,
            episodeNumber: entry.episode?.episode_number || null,
            seasonNumber: entry.episode?.season?.season_number || null,
            progress: Math.min(Math.round((entry.watch_duration_seconds / totalDur) * 100), 100),
            watchDuration: entry.watch_duration_seconds,
            totalDuration: entry.total_duration_seconds,
            completed: entry.completed,
            lastWatchedAt: entry.last_watched_at
          }
        })
        .filter(Boolean)
    } catch (error) {
      console.error('Error in getWatchHistory:', error)
      return []
    }
  },

  /**
   * Get a publisher by brand_slug for public studio pages.
   * Uses the `publisher_brand_pages` view which bypasses restrictive RLS
   * and only exposes public brand columns (no contact info, tax ID, etc.).
   */
  async getPublisherBySlug(slugOrId) {
    try {
      // Try brand_slug first via the public view
      let { data, error } = await supabase
        .from('publisher_brand_pages')
        .select('*')
        .eq('brand_slug', slugOrId)
        .maybeSingle()

      // Fallback: treat as publisher ID (useful in dev when brand_slug is null)
      if (!data && !error) {
        const idResult = await supabase
          .from('publisher_brand_pages')
          .select('*')
          .eq('id', slugOrId)
          .maybeSingle()
        data = idResult.data
        error = idResult.error
      }

      if (error || !data) {
        console.error('Error fetching publisher:', error)
        return null
      }

      return {
        id: data.id,
        companyName: data.company_name,
        brandSlug: data.brand_slug,
        logoUrl: data.brand_logo_url || data.logo_url || null,
        bannerUrl: data.brand_banner_url || null,
        description: data.brand_description || data.description || '',
        aboutUs: data.brand_about_us || '',
        socialLinks: data.brand_social_links || {},
        brandPagePublished: data.brand_page_published ?? false,
        featuredContentIds: data.featured_content_ids || [],
        followerCount: data.follower_count || 0,
        contentCount: data.content_count || 0,
        totalViews: data.total_views || 0,
        isVerified: data.is_verified || false,
        website: data.website || null,
        businessType: data.business_type || null,
        status: data.status
      }
    } catch (error) {
      console.error('Error in getPublisherBySlug:', error)
      return null
    }
  },

  /**
   * Get published works for a specific publisher
   */
  async getPublisherWorks(publisherId, { limit = 50, sortBy = 'newest' } = {}) {
    try {
      let query = supabase
        .from('works')
        .select(WORK_SELECT)
        .eq('publisher_id', publisherId)
        .eq('status', 'published')
        .limit(limit)

      if (sortBy === 'views' || sortBy === 'popular') {
        query = query.order('view_count', { ascending: false, nullsFirst: false })
      } else {
        query = query.order('created_at', { ascending: false })
      }

      const { data, error } = await query

      if (error) {
        console.error('Error fetching publisher works:', error)
        return []
      }

      return await transformWorksWithVideos(data || [])
    } catch (error) {
      console.error('Error in getPublisherWorks:', error)
      return []
    }
  },

  /**
   * Get viewer stats from real data
   */
  async getViewerStats(userId) {
    try {
      const [watchlistResult, historyResult, ratingsResult] = await Promise.all([
        supabase.from('user_watchlist').select('*', { count: 'exact', head: true }).eq('user_id', userId),
        supabase.from('user_watch_history').select('watch_duration_seconds, completed').eq('user_id', userId),
        supabase.from('user_ratings').select('*', { count: 'exact', head: true }).eq('user_id', userId)
      ])

      const history = historyResult.data || []
      const totalSeconds = history.reduce((sum, h) => sum + (h.watch_duration_seconds || 0), 0)
      const completedCount = history.filter(h => h.completed).length

      const hours = Math.floor(totalSeconds / 3600)
      const minutes = Math.floor((totalSeconds % 3600) / 60)

      return {
        watchlistCount: watchlistResult.count || 0,
        videosWatched: history.length,
        totalWatchTime: hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`,
        completedCount,
        completionRate: history.length > 0 ? Math.round((completedCount / history.length) * 100) : 0,
        ratingsCount: ratingsResult.count || 0
      }
    } catch (error) {
      console.error('Error in getViewerStats:', error)
      return {
        watchlistCount: 0,
        videosWatched: 0,
        totalWatchTime: '0m',
        completedCount: 0,
        completionRate: 0,
        ratingsCount: 0
      }
    }
  }
}

export default contentService
